import React from "react";
import { NavLink, useNavigate, Outlet } from "react-router-dom";
import { useAdminAuth } from "@/lib/adminAuth";
import { Button } from "@/components/ui/button";
import { LayoutDashboard, ShieldCheck, LogOut, Activity, Users, Package, Banknote, AlertOctagon, SlidersHorizontal, Trash2, XCircle, Flag, Route } from "lucide-react";
import Logo from "@/components/Logo";
import SmsWarningBanner from "@/components/admin/SmsWarningBanner";

const NavItem = ({ to, icon: Icon, children, end = false, testid }) => (
  <NavLink
    to={to}
    end={end}
    data-testid={testid}
    className={({ isActive }) =>
      `flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition ${
        isActive
          ? "bg-slate-900 text-white"
          : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
      }`
    }
  >
    <Icon className="w-4 h-4"/>
    <span>{children}</span>
  </NavLink>
);

const AdminLayout = () => {
  const { admin, logout } = useAdminAuth();
  const nav = useNavigate();

  const onLogout = async () => {
    await logout();
    nav("/admin/login");
  };

  return (
    <div className="min-h-screen bg-slate-50 flex">
      <aside className="w-60 shrink-0 border-r border-slate-200 bg-white flex flex-col" data-testid="admin-sidebar">
        <div className="px-4 py-4 border-b border-slate-100">
          <div className="flex items-center gap-2">
            <Logo size={28} variant="dark"/>
            <div>
              <div className="font-display font-bold text-sm">CarryIt</div>
              <div className="text-[10px] uppercase tracking-wider text-slate-400">Back office</div>
            </div>
          </div>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          <NavItem to="/admin" end icon={LayoutDashboard} testid="nav-dashboard">Dashboard</NavItem>
          <NavItem to="/admin/kyc" icon={ShieldCheck} testid="nav-kyc">KYC verification</NavItem>
          <NavItem to="/admin/users" icon={Users} testid="nav-users">Users</NavItem>
          <NavItem to="/admin/parcels" icon={Package} testid="nav-parcels">Parcels</NavItem>
          <NavItem to="/admin/trips" icon={Route} testid="nav-trips">Trips</NavItem>
          <NavItem to="/admin/payouts" icon={Banknote} testid="nav-payouts">Payouts</NavItem>
          <NavItem to="/admin/cod-outstanding" icon={Banknote} testid="nav-cod-outstanding">COD outstanding</NavItem>
          <NavItem to="/admin/disputes" icon={AlertOctagon} testid="nav-disputes">Disputes</NavItem>
          <NavItem to="/admin/cancellations" icon={XCircle} testid="nav-cancellations">Cancellations</NavItem>
          <NavItem to="/admin/chat-reports" icon={Flag} testid="nav-chat-reports">Chat reports</NavItem>
          <NavItem to="/admin/pricing" icon={SlidersHorizontal} testid="nav-pricing">Pricing config</NavItem>
          <div className="pt-3 mt-3 border-t border-slate-100 space-y-1">
            <NavItem to="/admin/activity" icon={Activity} testid="nav-activity">Activity log</NavItem>
            <NavItem to="/admin/test-data" icon={Trash2} testid="nav-test-data">Test data cleanup</NavItem>
          </div>
        </nav>
        <div className="p-3 border-t border-slate-100">
          <div className="text-[11px] text-slate-500 mb-2 truncate" data-testid="admin-current-email">{admin?.email}</div>
          <Button
            variant="outline"
            size="sm"
            data-testid="admin-logout-btn"
            onClick={onLogout}
            className="w-full rounded-lg justify-start text-slate-600"
          >
            <LogOut className="w-3.5 h-3.5 mr-1"/> Sign out
          </Button>
        </div>
      </aside>
      <main className="flex-1 min-w-0 overflow-x-auto">
        <SmsWarningBanner />
        <Outlet />
      </main>
    </div>
  );
};

export default AdminLayout;
